// src/components/aols/profile/AboutRWH.tsx
import React from "react";
import { Layout, Typography, Button } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import logo from "/logo.jpg";
import { Footer } from "../../dev/utils/Footer";
import styled from "styled-components";

// Define TypeScript interfaces
interface AboutRWHProps {}

// Ant Design components with TypeScript typing
const { Title, Paragraph } = Typography;

// Styled-components for styling
const FullHeightLayout = styled(Layout)`
  min-height: 100vh;
  background: linear-gradient(180deg, #000000 0%, #1a1a1a 100%);
`;

const StyledHeader = styled.header`
  display: flex;
  justify-content: center;
  align-items: center;
  background: #1a1a1a;
  color: white;
  height: 64px;
  padding: 10px 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 100;

  img {
    height: 50px;
    margin-right: 20px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    padding: 10px;
  }
`;

const CustomButton = styled(Button)`
  margin-right: 20px;
  background: #3552fc; /* Deep blue */
  color: white;
  border: none;
  padding: 5px 15px;
  border-radius: 5px;
  transition: background 0.3s ease;

  &:hover {
    background: #40a9ff; /* Lighter blue */
    color: white;
  }

  @media (max-width: 768px) {
    padding: 5px 10px;
    font-size: 14px;
  }
`;

const MainContent = styled(Layout.Content)`
  padding: 100px 20px 20px; /* Increased top padding to account for TopRightContainer */
  text-align: center;
  flex: 1;
  min-height: calc(100vh - 134px); /* Adjust for footer */

  @media (max-width: 768px) {
    padding: 120px 10px 10px; /* Adjusted for mobile */
  }
`;

const ProfileContainer = styled.div`
  background: #1a1a1a; /* Dark background */
  color: #0427f4; /* OLS blue */
  margin: 0 auto;
  max-width: 900px;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important; /* Sunlight orange for active links */
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important; /* Sunlight yellow on hover */
  }

  .breadcrumb-separator {
    color: #fff; /* White separator for contrast */
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important; /* White for non-active items */
  }
`;

const SectionTitle = styled(Title)`
  color: #ffaa00 !important; /* Sunlight orange */
  font-size: 2.5em !important;
  margin-bottom: 20px !important;

  @media (max-width: 768px) {
    font-size: 1.8em !important;
  }
`;

const FeatureCard = styled.div`
  background: #fce248; /* Sunlight yellow */
  padding: 20px;
  border-radius: 8px;
  border: 2px solid #ffcc00;
  text-align: left;
  color: #000 !important;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const CallToActionButton = styled(Button)`
  margin-top: 30px;
  background: linear-gradient(45deg, #ffcc00, #ffaa00, #ff8800);
  color: white;
  border: none;
  font-size: 20px;
  padding: 15px 40px;
  border-radius: 25px;
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.5);
  font-weight: bold;
  transition: background 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;

  &:hover {
    background: linear-gradient(45deg, #ffaa00, #ff8800, #ff6600);
    box-shadow: 0 6px 20px rgba(255, 204, 0, 0.7);
    transform: translateY(-2px);
    color: white;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 10px 30px;
  }
`;

const StyledFooter = styled(Layout.Footer)`
  background: #ffcc00; /* Sunlight yellow */
  color: #333;
  text-align: center;
  padding: 15px 0;
  font-size: 14px;
  position: relative;
  bottom: 0;
  width: 100%;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);

  a {
    color: #333;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

const AboutRWHPage: React.FC<AboutRWHProps> = () => {
  const images = [
    "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F2739.306.RWH.png?alt=media&token=92e6bdd9-2746-4068-b1f5-8829199d0532",
  ];

  return (
    <FullHeightLayout>
      <Helmet>
        <title>About ReiWenHo - OLS Inuo Instructor OLS Basic Master | onelightsystem OLS</title>
        <meta
          name="description"
          content="Learn more about ReiWenHo (Jennifer Ho), OLS Inuo Instructor OLS Basic Master and founder of Artis School of Music, with 6,674 Light Minutes. Discover her journey in music education and meditation."
        />
        <meta
          name="keywords"
          content="ReiWenHo, Jennifer Ho, OLS Inuo Instructor, OLS Basic Master, Sun Light Meditation, onelightsystem OLS, Artis School of Music, Music Education, Belmont Community"
        />
        <meta name="author" content="onelightsystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="About ReiWenHo - OLS Inuo Instructor OLS Basic Master | onelightsystem OLS" />
        <meta
          property="og:description"
          content="Learn more about ReiWenHo (Jennifer Ho), OLS Inuo Instructor OLS Basic Master and founder of Artis School of Music, with 6,674 Light Minutes. Discover her journey in music education and meditation."
        />
        <meta property="og:url" content="https://www.olsme.com/about-rwh" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={images[0]} />
        <meta property="og:image:alt" content="ReiWenHo, OLS Inuo Instructor, teaching Sun Light Meditation" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About ReiWenHo - OLS Inuo Instructor OLS Basic Master | onelightsystem OLS" />
        <meta
          name="twitter:description"
          content="Learn more about ReiWenHo (Jennifer Ho), OLS Inuo Instructor OLS Basic Master and founder of Artis School of Music, with 6,674 Light Minutes. Discover her journey in music education and meditation."
        />
        <meta name="twitter:image" content={images[0]} />
        <link rel="canonical" href="https://www.olsme.com/about-rwh" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.olsme.com/",
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "ReiWenHo",
                "item": "https://www.olsme.com/rwh",
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "About",
                "item": "https://www.olsme.com/about-rwh",
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Jennifer Ho",
            "alternateName": "ReiWenHo",
            "jobTitle": "OLS Inuo Instructor OLS Basic Master",
            "description": "ReiWenHo (Jennifer Ho), an OLS Inuo Instructor OLS Basic Master with 6,674 Light Minutes, contributes to Sun Light Meditation education at onelightsystem OLS. She is also the founder of Artis School of Music.",
            "url": "https://www.olsme.com/about-rwh",
            "image": images[0],
            "birthPlace": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "San Mateo",
                "addressRegion": "CA",
                "addressCountry": "US",
              },
            },
            "nationality": "American",
            "affiliation": {
              "@type": "Organization",
              "name": "onelightsystem OLS",
              "url": "https://www.olsme.com",
            },
            "knowsAbout": [
              "Sun Light Meditation",
              "Music Education",
              "Artis School of Music",
              "Belmont Community",
            ],
            "sameAs": [
              "https://web.facebook.com/reiwen.ho",
              "https://www.linkedin.com/in/jennifer-ho-045aa981",
              "https://www.artismymusic.com/our-team?name=Jennifer%20Ho",
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "onelightsystem OLS",
            "url": "https://www.olsme.com",
            "logo": "https://www.olsme.com/logo.jpg",
            "description": "A global platform advancing Sun Light Meditation and fostering a Light Civilization.",
            "founder": {
              "@type": "Person",
              "name": "Nazar Pankiv",
              "alternateName": "Asvitloaten",
            },
          })}
        </script>
      </Helmet>

      {/* Header with Logo and Statistic Page Button */}
      <StyledHeader>
        <Link to="/">
          <img src={logo} alt="OneLightSystem OLS Logo" />
        </Link>
        <CustomButton aria-label="Navigate to OLS Statistic page">
          <Link to="/Stat">OLS Statistic</Link>
        </CustomButton>
      </StyledHeader>

      {/* Main Content */}
      <MainContent>
        <ProfileContainer>
          <CustomBreadcrumb>
            <span className="breadcrumb-item">
              <Link to="/" className="breadcrumb-link">
                Home
              </Link>
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-item">
              <Link to="/rwh" className="breadcrumb-link">
                ReiWenHo
              </Link>
            </span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-item breadcrumb-non-active">
              About
            </span>
          </CustomBreadcrumb>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <SectionTitle level={1}>About Jennifer Ho</SectionTitle>
            <CustomButton aria-label="View OLS Inuo ReiWenHo profile">
              <Link to="/rwh">OLS Inuo ReiWenHo</Link>
            </CustomButton>
          </div>

          <FeatureCard>
            <Paragraph style={{ color: "#000", fontSize: "16px" }}>
              Experienced Director with a demonstrated history of working in the education management industry. Skilled in Entrepreneurship, Strategy, Product Management, Teaching, and Start-ups. Strong professional graduated from City University of New York-Queens College.
            </Paragraph>
            <Paragraph style={{ color: "#000", fontSize: "16px" }}>
              Arte Piano Quartet Violanist 2006 San Mateo Ca<br />
              First Violinist Stockton Symphony 2006 - 2008 Stockton, California Area<br />
              Founder and Teacher Director Happy Mozart Music School 2007 - 2012 San Mateo
            </Paragraph>
            <Paragraph style={{ color: "#000", fontSize: "16px" }}>
              Jennifer Ho is the Founder Director Artis school of Music since 2015 Jennifer established a new Belmont location to dedicate her knowledge and experience to create and maintain music development in Belmont community and beyond. in 2022 Jennifer lunched a new web platform that will help make it much more convenient to provide excellent music education experience. Jennifer though many students, facilitated regularly community concerts that enhancing local cultural development
            </Paragraph>
            <Paragraph style={{ color: "#000", fontSize: "16px" }}>
              in 2024 Jennifer is focusing more on teaching school Teachers and Improving Web platform with adding more innovative Global expansion this coming season! Subscribe to Our E-News to stay updated it.. New Prospects and Our dear Past Students Do not wait .. You can contact Us Now before teachers schedule will be unavailable for upcoming time…! We exited to announce Guest feature that will allow new prospect to join Our Team in growing Career by becoming Artis Teacher into Certified credentials
            </Paragraph>
            <Paragraph style={{ color: "#000", fontSize: "16px" }}>
              <a href="https://www.artismymusic.com/our-team?name=Jennifer%20Ho" target="_blank" rel="noopener noreferrer">
                Artis School of Music
              </a>
            </Paragraph>
          </FeatureCard>

          <CallToActionButton aria-label="Apply to become an OLS student">
            <Link to="https://www.olsme.com/register/olssregister" target="_blank">
              Apply to Become an OLS Student
            </Link>
          </CallToActionButton>
        </ProfileContainer>
      </MainContent>

      <StyledFooter>
        <Footer />
      </StyledFooter>
    </FullHeightLayout>
  );
};

export default AboutRWHPage;